import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import api from '../lib/api';
import {
  Download, ArrowLeft, Loader2, Copy, Share2, Siren, X,
  AlertTriangle, FileWarning, ClipboardList, PenLine,
  MessageCircleQuestion, Send, FileDigit, UserRound, MonitorUp,
  Volume2, Pause, Columns2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { UrgencyBadge, EmergencyFlagBadge } from '../components/UrgencyBadge';
import { ImageViewer, CATEGORY_COLORS } from '../components/ImageViewer';
import { ComparisonView } from '../components/ComparisonView';
import { AnalysisReport } from '../components/AnalysisReport';
import { ScreenCaptureDialog } from '../components/ScreenCaptureDialog';
import { toast } from 'sonner';

export const getLesionMarkers = (report) => {
  if (!report) return [];
  if (report.lesion_markers?.length) return report.lesion_markers;
  return (report.bone_lesion_markers || []).map((m) => ({
    ...m,
    category: m.category || 'BONE',
    image_index: m.image_index || 0,
  }));
};

const playAlertSound = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    [0, 0.4, 0.8].forEach((delay) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.12, ctx.currentTime + delay);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + 0.35);
      osc.start(ctx.currentTime + delay);
      osc.stop(ctx.currentTime + delay + 0.4);
    });
  } catch (e) {
    // Audio may be blocked - non-critical
  }
};

const AnalysisDetailPage = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [sharing, setSharing] = useState(false);

  // Annotations
  const [annotations, setAnnotations] = useState([]);
  const [annotationMode, setAnnotationMode] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [pendingRect, setPendingRect] = useState(null);
  const [annLabel, setAnnLabel] = useState('');
  const [annCategory, setAnnCategory] = useState('BONE');
  const [savingAnn, setSavingAnn] = useState(false);

  // Follow-up
  const [followups, setFollowups] = useState([]);
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [followupAttachments, setFollowupAttachments] = useState([]); // [{id, file, previewUrl}]
  const [followupCaptureOpen, setFollowupCaptureOpen] = useState(false);
  const followupEndRef = useRef(null);
  const MAX_FOLLOWUP_ATTACHMENTS = 4;

  // Alert
  const [alertDismissed, setAlertDismissed] = useState(false);
  const soundPlayed = useRef(false);

  // TTS (report read aloud)
  const [ttsLoading, setTtsLoading] = useState(false);
  const [ttsPlaying, setTtsPlaying] = useState(false);
  const [ttsReady, setTtsReady] = useState(false);
  const ttsAudioRef = useRef(null);
  const ttsUrlRef = useRef(null);

  const stopTts = useCallback(() => {
    if (ttsAudioRef.current) {
      try {
        ttsAudioRef.current.pause();
        ttsAudioRef.current.currentTime = 0;
      } catch (e) {
        // Audio may already be released - non-critical
      }
      ttsAudioRef.current = null;
    }
    if (ttsUrlRef.current) {
      URL.revokeObjectURL(ttsUrlRef.current);
      ttsUrlRef.current = null;
    }
    setTtsPlaying(false);
    setTtsReady(false);
  }, []);

  // Playbook constraint: stop audio on unmount, navigation away, tab hidden or window blur
  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden) stopTts();
    };
    const onBlur = () => stopTts();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('blur', onBlur);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', onBlur);
      stopTts();
    };
  }, [stopTts]);

  const handleTts = async () => {
    // Playing -> pause
    if (ttsAudioRef.current && ttsPlaying) {
      ttsAudioRef.current.pause();
      setTtsPlaying(false);
      return;
    }
    // Paused with audio loaded -> resume
    if (ttsAudioRef.current) {
      try {
        await ttsAudioRef.current.play();
        setTtsPlaying(true);
      } catch (e) {
        stopTts();
        toast.error(t('tts.error'));
      }
      return;
    }
    // First play -> fetch MP3 from backend (cached server-side)
    setTtsLoading(true);
    try {
      const res = await api.get(`/analyses/${id}/tts`, {
        params: { language: analysis?.language || 'fr' },
        responseType: 'blob',
        timeout: 120000,
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'audio/mpeg' }));
      ttsUrlRef.current = url;
      const audio = new Audio(url);
      ttsAudioRef.current = audio;
      audio.onended = () => {
        setTtsPlaying(false);
        if (ttsAudioRef.current) {
          try { ttsAudioRef.current.currentTime = 0; } catch (e) { /* non-critical */ }
        }
      };
      audio.onerror = () => {
        stopTts();
        toast.error(t('tts.error'));
      };
      await audio.play();
      setTtsReady(true);
      setTtsPlaying(true);
    } catch (error) {
      console.error('TTS error:', error);
      stopTts();
      toast.error(t('tts.error'));
    } finally {
      setTtsLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalysis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchAnalysis = async () => {
    try {
      const res = await api.get(`/analyses/${id}`);
      setAnalysis(res.data);
      setAnnotations(res.data.user_annotations || []);
      setFollowups(res.data.followups || []);
    } catch (error) {
      console.error('Failed to load analysis:', error);
      toast.error(t('errors.load_failed'));
    } finally {
      setLoading(false);
    }
  };

  // Critical alert sound (once)
  useEffect(() => {
    if (!analysis || soundPlayed.current) return;
    const level = analysis.report_json?.emergency_level;
    const flag = analysis.report_json?.emergency_flag;
    if (level === 'CRITICAL' || (flag === 'YES' && level === 'HIGH')) {
      soundPlayed.current = true;
      playAlertSound();
    }
  }, [analysis]);

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/analyses/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `emergency_report_${id.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF');
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error(t('errors.pdf_failed'));
    } finally {
      setDownloading(false);
    }
  };

  const handleShare = async () => {
    setSharing(true);
    try {
      const res = await api.post(`/analyses/${id}/share`);
      const url = `${window.location.origin}/shared/${res.data.share_token}`;
      await navigator.clipboard.writeText(url);
      toast.success(t('report.share_copied'));
    } catch (error) {
      console.error('Share error:', error);
      toast.error(t('errors.load_failed'));
    } finally {
      setSharing(false);
    }
  };

  const copyReportText = () => {
    if (!analysis?.report_json) return;
    const report = analysis.report_json;
    const text = [
      `Emergency: ${report.emergency_flag} - ${report.emergency_level}`,
      `Modality: ${report.modality_guess}`,
      `Region: ${report.region}`,
      '',
      'Critical Findings:',
      ...(report.critical_findings || []).map((f) => `- ${f}`),
      '',
      'Impression:',
      ...(report.hierarchized_impression || []).map((f) => `- ${f}`),
      '',
      'Limitations:',
      ...(report.limitations || []).map((f) => `- ${f}`),
      '',
      report.disclaimer || '',
    ].join('\n');
    navigator.clipboard.writeText(text);
    toast.success('Copied');
  };

  // ===== Annotations =====
  const saveAnnotations = async (list) => {
    setSavingAnn(true);
    try {
      const res = await api.put(`/analyses/${id}/annotations`, { annotations: list });
      setAnnotations(res.data.annotations);
      return true;
    } catch (error) {
      console.error('Annotation save error:', error);
      toast.error(t('errors.load_failed'));
      return false;
    } finally {
      setSavingAnn(false);
    }
  };

  const handleDrawComplete = useCallback((rect) => {
    setPendingRect(rect);
    setAnnLabel('');
    setAnnCategory('BONE');
  }, []);

  const confirmAnnotation = async () => {
    if (!pendingRect || !annLabel.trim()) return;
    const newAnn = {
      ...pendingRect,
      label: annLabel.trim(),
      category: annCategory,
    };
    const ok = await saveAnnotations([...annotations, newAnn]);
    if (ok) {
      toast.success(t('annotations.saved'));
      setPendingRect(null);
    }
  };

  const handleDeleteAnnotation = async (annId) => {
    const ok = await saveAnnotations(annotations.filter((a) => a.id !== annId));
    if (ok) toast.success(t('annotations.deleted'));
  };

  // ===== Follow-up =====
  const addAttachment = (file) => {
    if (followupAttachments.length >= MAX_FOLLOWUP_ATTACHMENTS) {
      toast.error(t('followup.attachments_max'));
      return;
    }
    setFollowupAttachments((prev) => [
      ...prev,
      { id: `${Date.now()}-${Math.random()}`, file, previewUrl: URL.createObjectURL(file), caption: '' },
    ]);
    toast.success(t('followup.attachment_added'));
  };

  const updateAttachmentCaption = (attId, value) => {
    setFollowupAttachments((prev) =>
      prev.map((a) => (a.id === attId ? { ...a, caption: value.slice(0, 200) } : a))
    );
  };

  const removeAttachment = (attId) => {
    setFollowupAttachments((prev) => {
      const target = prev.find((a) => a.id === attId);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((a) => a.id !== attId);
    });
  };

  const clearAllAttachments = () => {
    setFollowupAttachments((prev) => {
      prev.forEach((a) => a.previewUrl && URL.revokeObjectURL(a.previewUrl));
      return [];
    });
  };

  const handleFollowupPaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files = [];
    for (const item of items) {
      if (item.type && item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          const ext = (file.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
          files.push(new File([file], `colle_${Date.now()}_${files.length}.${ext}`, { type: file.type }));
        }
      }
    }
    if (files.length > 0) {
      e.preventDefault();
      files.forEach((f) => addAttachment(f));
    }
  };

  const handleAsk = async () => {
    const q = question.trim();
    if (!q || asking) return;
    setAsking(true);
    try {
      const fd = new FormData();
      fd.append('question', q);
      fd.append('language', analysis?.language || 'fr');
      followupAttachments.forEach((a) => fd.append('images', a.file));
      if (followupAttachments.length > 0) {
        fd.append('captions', JSON.stringify(followupAttachments.map((a) => a.caption || '')));
      }
      const res = await api.post(`/analyses/${id}/followup`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      setFollowups((prev) => [...prev, res.data]);
      setQuestion('');
      clearAllAttachments();
      setTimeout(() => followupEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (error) {
      console.error('Follow-up error:', error);
      toast.error(t('followup.error'));
    } finally {
      setAsking(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 size={32} className="text-cyan-400 animate-spin" />
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <FileWarning size={48} className="text-muted-foreground" />
        <p className="text-muted-foreground">Analysis not found</p>
        <Button variant="outline" onClick={() => navigate('/')}>
          <ArrowLeft size={16} className="mr-2" /> {t('report.new_analysis')}
        </Button>
      </div>
    );
  }

  const report = analysis.report_json || {};
  const level = report.emergency_level || 'MODERATE';
  const flag = report.emergency_flag || 'UNCERTAIN';
  const markers = getLesionMarkers(report);
  const images = analysis.images?.length
    ? analysis.images
    : [{ image_base64: analysis.image_base64, image_mime: analysis.image_mime }];
  const priorImages = analysis.prior_images || [];
  const dicomMeta = analysis.dicom_metadata || [];
  const isCriticalAlert = level === 'CRITICAL' || (flag === 'YES' && level === 'HIGH');

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Emergency Summary Strip */}
      <div
        className="-mx-4 md:-mx-6 px-4 md:px-6 py-3 bg-[hsl(210,18%,7%)]/90 backdrop-blur border-b border-border mb-4 sticky top-16 z-20"
        data-testid="emergency-summary-strip"
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="text-muted-foreground hover:text-foreground"
              data-testid="back-button"
            >
              <ArrowLeft size={16} />
            </Button>
            <UrgencyBadge level={level} size="lg" />
            <EmergencyFlagBadge flag={flag} />
            <span className="text-xs text-muted-foreground hidden sm:inline">
              {report.modality_guess} | {report.region}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleTts}
              disabled={ttsLoading}
              className="text-xs gap-1.5 border-teal-500/40 text-teal-300 hover:bg-teal-500/10 hover:text-teal-200"
              data-testid="tts-listen-button"
            >
              {ttsLoading ? (
                <Loader2 size={14} className="animate-spin" />
              ) : ttsPlaying ? (
                <Pause size={14} />
              ) : (
                <Volume2 size={14} />
              )}
              {ttsLoading
                ? t('tts.loading')
                : ttsPlaying
                  ? t('tts.pause')
                  : ttsReady
                    ? t('tts.resume')
                    : t('tts.listen')}
            </Button>
            <Button variant="outline" size="sm" onClick={copyReportText} className="text-xs gap-1.5" data-testid="copy-report-button">
              <Copy size={14} />
              {t('report.copy')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleShare}
              disabled={sharing}
              className="text-xs gap-1.5"
              data-testid="share-button"
            >
              {sharing ? <Loader2 size={14} className="animate-spin" /> : <Share2 size={14} />}
              {t('report.share')}
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={handleDownloadPdf}
              disabled={downloading}
              className="text-xs gap-1.5 bg-cyan-500 hover:bg-cyan-400 text-[hsl(210,25%,8%)]"
              data-testid="pdf-download-button"
            >
              {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              {downloading ? t('report.downloading') : t('report.download_pdf')}
            </Button>
          </div>
        </div>

        {(report.critical_findings?.length > 0) && (
          <div className="mt-3 pl-10">
            <div className="flex flex-wrap gap-2">
              {report.critical_findings.slice(0, 3).map((finding, i) => (
                <span key={i} className="text-xs text-red-300 bg-red-500/10 px-2 py-1 rounded-md">
                  <AlertTriangle size={10} className="inline mr-1" />
                  {finding.length > 80 ? finding.slice(0, 80) + '...' : finding}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* CRITICAL ALERT BANNER */}
      {isCriticalAlert && !alertDismissed && (
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          className="mb-4 p-4 rounded-xl bg-red-500/15 border-2 border-red-500/70 flex items-center gap-4 animate-critical-pulse"
          data-testid="critical-alert-banner"
        >
          <div className="w-11 h-11 rounded-full bg-red-500/25 flex items-center justify-center shrink-0">
            <Siren size={22} className="text-red-400" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-bold text-red-300 tracking-wide">{t('alert.title')}</p>
            <p className="text-xs text-red-300/80 mt-0.5">{t('alert.desc')}</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setAlertDismissed(true)}
            className="text-red-300 hover:text-red-200 h-8 w-8 p-0"
            data-testid="dismiss-alert-button"
          >
            <X size={16} />
          </Button>
        </motion.div>
      )}

      <div className="grid grid-cols-12 gap-4 md:gap-6">
        {/* Left: Viewer + Metadata */}
        <div className="col-span-12 xl:col-span-5">
          <Card className="bg-[hsl(210,18%,9%)] border-border overflow-hidden">
            <CardContent className="p-0">
              {compareMode && priorImages.length > 0 ? (
                <ComparisonView
                  images={images}
                  priorImages={priorImages}
                  onExit={() => setCompareMode(false)}
                  t={t}
                />
              ) : (
                <ImageViewer
                  images={images}
                  priorImages={priorImages}
                  markers={markers}
                  annotations={annotations}
                  annotationMode={annotationMode}
                  onDrawComplete={handleDrawComplete}
                  onDeleteAnnotation={handleDeleteAnnotation}
                  t={t}
                />
              )}
            </CardContent>
          </Card>

          {/* Side-by-side comparison toggle (only when a prior exam exists) */}
          {priorImages.length > 0 && !compareMode && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCompareMode(true)}
              className="w-full mt-3 gap-2 text-xs border-teal-500/40 text-teal-300 hover:bg-teal-500/10 hover:text-teal-200"
              data-testid="comparison-toggle-button"
            >
              <Columns2 size={14} />
              {t('compare.toggle')}
            </Button>
          )}

          {/* Annotation mode toggle */}
          <Button
            variant={annotationMode ? 'default' : 'outline'}
            size="sm"
            onClick={() => setAnnotationMode((v) => !v)}
            className={`w-full mt-3 gap-2 text-xs ${annotationMode ? 'bg-cyan-500 text-[hsl(210,25%,8%)] hover:bg-cyan-400' : ''}`}
            data-testid="annotation-mode-toggle"
          >
            <PenLine size={14} />
            {annotationMode ? t('annotations.mode_off') : t('annotations.mode_on')}
          </Button>

          {/* DICOM metadata */}
          {dicomMeta.length > 0 && (
            <Card className="mt-4 bg-[hsl(210,18%,9%)] border-border" data-testid="dicom-metadata-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <FileDigit size={16} className="text-teal-400" />
                  {t('dicom.title')}
                </CardTitle>
              </CardHeader>
              <CardContent className="py-2">
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(dicomMeta[0]).map(([k, v]) => (
                    <div key={k}>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{t(`dicom.${k}`, k)}</p>
                      <p className="text-xs font-medium mt-0.5">{v}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Metadata */}
          <Card className="mt-4 bg-[hsl(210,18%,9%)] border-border">
            <CardContent className="py-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{t('report.modality')}</p>
                  <p className="text-sm font-medium mt-0.5">{report.modality_guess || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{t('report.region')}</p>
                  <p className="text-sm font-medium mt-0.5">{report.region || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{t('history.date')}</p>
                  <p className="text-sm font-medium mt-0.5">
                    {analysis.created_at ? new Date(analysis.created_at).toLocaleString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{t('report.emergency')}</p>
                  <div className="mt-0.5"><EmergencyFlagBadge flag={flag} /></div>
                </div>
                {analysis.user_name && (
                  <div data-testid="analyzed-by">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{t('report.analyzed_by')}</p>
                    <p className="text-sm font-medium mt-0.5 flex items-center gap-1.5">
                      <UserRound size={13} className="text-cyan-400" />
                      {analysis.user_name}
                    </p>
                  </div>
                )}
                {analysis.num_images > 1 && (
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Images</p>
                    <p className="text-sm font-medium mt-0.5">{analysis.num_images}</p>
                  </div>
                )}
              </div>

              {analysis.clinical_context && (
                <>
                  <Separator className="bg-border" />
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">
                      {t('context.title')}
                    </p>
                    <p className="text-xs text-foreground/80">{analysis.clinical_context}</p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right: Report + Follow-up */}
        <div className="col-span-12 xl:col-span-7">
          <Card className="bg-[hsl(210,18%,9%)] border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <ClipboardList size={20} className="text-cyan-400" />
                {t('report.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <AnalysisReport report={report} t={t} />
            </CardContent>
          </Card>

          {/* Follow-up Q&A (second reading) */}
          <Card className="mt-4 bg-[hsl(210,18%,9%)] border-border" data-testid="followup-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <MessageCircleQuestion size={17} className="text-teal-400" />
                {t('followup.title')}
              </CardTitle>
              <p className="text-xs text-muted-foreground">{t('followup.subtitle')}</p>
            </CardHeader>
            <CardContent className="space-y-3">
              {followups.length === 0 && !asking && (
                <p className="text-xs text-muted-foreground italic text-center py-2">{t('followup.empty')}</p>
              )}
              <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
                {followups.map((f, i) => (
                  <div key={f.id || i} className="space-y-2" data-testid={`followup-item-${i}`}>
                    <div className="flex justify-end">
                      <div className="max-w-[85%] bg-cyan-500/10 border border-cyan-500/20 rounded-lg rounded-br-sm px-3 py-2">
                        {(f.images?.length > 0 || f.image_base64) && (
                          <div className="flex flex-wrap gap-1.5 mb-1.5">
                            {(f.images?.length > 0
                              ? f.images
                              : [{ image_base64: f.image_base64, image_mime: f.image_mime }]
                            ).map((img, j) => (
                              <div key={j} className="space-y-0.5">
                                <img
                                  src={`data:${img.image_mime || 'image/png'};base64,${img.image_base64}`}
                                  alt={`Attached capture ${j + 1}`}
                                  className="rounded-md max-h-24 border border-cyan-500/20"
                                  data-testid={`followup-attachment-${i}-${j}`}
                                />
                                {img.caption && (
                                  <p className="text-[9px] text-cyan-300/80 italic max-w-[140px] truncate" data-testid={`followup-attachment-caption-view-${i}-${j}`}>
                                    {img.caption}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        <p className="text-xs text-foreground/90">{f.question}</p>
                        {f.asked_by && (
                          <p className="text-[9px] text-muted-foreground mt-1 text-right">{f.asked_by}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex justify-start">
                      <div className="max-w-[85%] bg-[hsl(210,14%,12%)] border border-border rounded-lg rounded-bl-sm px-3 py-2">
                        <p className="text-xs text-foreground/85 whitespace-pre-wrap leading-relaxed">{f.answer}</p>
                      </div>
                    </div>
                  </div>
                ))}
                {asking && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 size={14} className="animate-spin text-cyan-400" />
                    {t('followup.thinking')}
                  </div>
                )}
                <div ref={followupEndRef} />
              </div>
              {/* Attachment chips with per-image captions */}
              {followupAttachments.length > 0 && (
                <div className="space-y-2" data-testid="followup-attachments-row">
                  {followupAttachments.map((att, k) => (
                    <div
                      key={att.id}
                      className="flex items-center gap-2 p-1.5 pr-2 rounded-lg bg-[hsl(210,14%,12%)] border border-cyan-500/25"
                      data-testid={`followup-attachment-chip-${k}`}
                    >
                      <img
                        src={att.previewUrl}
                        alt={`Attachment ${k + 1}`}
                        className="h-9 w-12 object-cover rounded shrink-0"
                      />
                      <Input
                        value={att.caption}
                        onChange={(e) => updateAttachmentCaption(att.id, e.target.value)}
                        placeholder={t('followup.caption_placeholder')}
                        maxLength={200}
                        className="h-7 flex-1 text-[11px] bg-[hsl(210,18%,8%)] border-[hsl(210,14%,18%)]"
                        data-testid={`followup-attachment-caption-${k}`}
                      />
                      <button
                        onClick={() => removeAttachment(att.id)}
                        className="w-5 h-5 shrink-0 rounded-full bg-[hsl(210,14%,18%)] hover:bg-red-500/70 text-muted-foreground hover:text-white flex items-center justify-center transition-colors duration-150"
                        data-testid={`followup-attachment-remove-${k}`}
                      >
                        <X size={11} />
                      </button>
                    </div>
                  ))}
                  <span className="text-[10px] text-cyan-300">
                    {followupAttachments.length}/{MAX_FOLLOWUP_ATTACHMENTS} — {t('followup.attachment')}
                  </span>
                </div>
              )}
              <div className="flex gap-2">
                <Input
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAsk()}
                  onPaste={handleFollowupPaste}
                  placeholder={t('followup.placeholder')}
                  disabled={asking}
                  className="h-10 text-sm bg-[hsl(210,18%,7%)] border-[hsl(210,14%,18%)]"
                  data-testid="followup-question-input"
                />
                <Button
                  variant="outline"
                  onClick={() => setFollowupCaptureOpen(true)}
                  disabled={asking || followupAttachments.length >= MAX_FOLLOWUP_ATTACHMENTS}
                  className="h-10 w-10 p-0 shrink-0 border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/10"
                  title={t('followup.attach_capture')}
                  data-testid="followup-capture-button"
                >
                  <MonitorUp size={15} />
                </Button>
                <Button
                  onClick={handleAsk}
                  disabled={asking || !question.trim()}
                  className="h-10 gap-1.5 bg-cyan-500 hover:bg-cyan-400 text-[hsl(210,25%,8%)] shrink-0"
                  data-testid="followup-send-button"
                >
                  {asking ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                </Button>
              </div>
              <p className="text-[9px] text-muted-foreground">{t('followup.attach_hint')}</p>
            </CardContent>
          </Card>

          {/* Action buttons */}
          <div className="mt-4 flex gap-3">
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="flex-1 gap-2"
              data-testid="new-analysis-button"
            >
              <ArrowLeft size={16} />
              {t('report.new_analysis')}
            </Button>
            <Button
              onClick={handleDownloadPdf}
              disabled={downloading}
              className="flex-1 gap-2 bg-cyan-500 hover:bg-cyan-400 text-[hsl(210,25%,8%)]"
              data-testid="pdf-download-button-bottom"
            >
              {downloading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
              {t('report.download_pdf')}
            </Button>
          </div>
        </div>
      </div>

      {/* Annotation dialog */}
      <Dialog open={!!pendingRect} onOpenChange={(open) => !open && setPendingRect(null)}>
        <DialogContent className="bg-[hsl(210,18%,9%)] border-border sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <PenLine size={16} className="text-cyan-400" />
              {t('annotations.add_title')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">{t('annotations.label')}</Label>
              <Input
                value={annLabel}
                onChange={(e) => setAnnLabel(e.target.value)}
                placeholder={t('annotations.label_placeholder')}
                className="h-10 text-sm bg-[hsl(210,18%,7%)] border-[hsl(210,14%,18%)]"
                autoFocus
                data-testid="annotation-label-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">{t('annotations.category')}</Label>
              <Select value={annCategory} onValueChange={setAnnCategory}>
                <SelectTrigger className="h-10 text-sm bg-[hsl(210,18%,7%)] border-[hsl(210,14%,18%)]" data-testid="annotation-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[hsl(210,18%,10%)] border-border">
                  {Object.keys(CATEGORY_COLORS).filter((c) => c !== 'PNEUMOTHORAX' || true).map((cat) => (
                    <SelectItem key={cat} value={cat} className="text-sm">
                      <span className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ backgroundColor: CATEGORY_COLORS[cat] }} />
                        {t(`categories.${cat}`)}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingRect(null)} className="text-xs" data-testid="annotation-cancel-button">
              {t('annotations.cancel')}
            </Button>
            <Button
              onClick={confirmAnnotation}
              disabled={!annLabel.trim() || savingAnn}
              className="text-xs bg-cyan-500 hover:bg-cyan-400 text-[hsl(210,25%,8%)]"
              data-testid="annotation-save-button"
            >
              {savingAnn ? <Loader2 size={14} className="animate-spin" /> : t('annotations.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Screen capture dialog for follow-up attachments */}
      <ScreenCaptureDialog
        open={followupCaptureOpen}
        onOpenChange={setFollowupCaptureOpen}
        onCapture={(file) => addAttachment(file)}
        t={t}
      />
    </motion.div>
  );
};

export default AnalysisDetailPage;
